import { useCallback, useEffect, useRef, useState } from "react";
import { Tooltip, Typography } from "@mui/material";

/** Figma Tooltip — black pill, Lato 12/18, arrow on top (placement bottom). */
const FIGMA_TOOLTIP_SX = {
  bgcolor: "#000000",
  borderRadius: "4px",
  px: "12px",
  py: "12px",
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  fontSize: 12,
  lineHeight: "18px",
  letterSpacing: 0,
  color: "#E6E6E6",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.25)",
  "& .MuiTooltip-arrow": { color: "#000000" },
};

const TRUNCATED_CELL_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      ...FIGMA_TOOLTIP_SX,
      maxWidth: 288,
      wordBreak: "break-word",
    },
  },
};

/** Ellipsis with Figma tooltip — only opens when the text is actually truncated. */
export default function TruncatedCellLine({ text, tooltipTitle, placement = "bottom", sx }) {
  const textRef = useRef(null);
  const [open, setOpen] = useState(false);
  const title = tooltipTitle ?? text;

  const close = useCallback(() => setOpen(false), []);

  const handleShouldOpen = () => {
    const el = textRef.current;
    setOpen(!!(el && el.scrollWidth > el.clientWidth));
  };

  useEffect(() => {
    if (!open) return undefined;

    const dismiss = () => close();

    // Scroll does not bubble; capture phase catches scroll on any ancestor.
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);

    const el = textRef.current;
    let observer;
    if (el && typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry && !entry.isIntersecting) dismiss();
        },
        { threshold: 0 }
      );
      observer.observe(el);
    }

    return () => {
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
      observer?.disconnect();
    };
  }, [open, close]);

  return (
    <Tooltip
      title={title}
      arrow
      placement={placement}
      open={open}
      onClose={close}
      disableHoverListener
      disableFocusListener
      disableTouchListener
      slotProps={TRUNCATED_CELL_TOOLTIP_SLOT_PROPS}
      describeChild
    >
      <Typography
        ref={textRef}
        onMouseEnter={handleShouldOpen}
        onMouseLeave={close}
        sx={{
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
          ...sx,
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
}
